/**
 * JWT helpers. The session lifetime is read from the token itself instead of a
 * locally written timestamp: a homemade marker can be deleted by another tab,
 * and a missing marker used to be read as "infinitely old session".
 */

/** Decodes the payload of a JWT. Returns null when the token is absent or malformed. */
const decodeJwtPayload = (token) => {
  if (typeof token !== 'string') return null;

  const segments = token.split('.');
  if (segments.length < 2) return null;

  try {
    const normalized = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch (error) {
    return null;
  }
};

/** Epoch ms when the token expires, or null when the token carries no usable `exp`. */
export const getExpiryMs = (token) => {
  const exp = decodeJwtPayload(token)?.exp;
  return Number.isFinite(exp) ? exp * 1000 : null;
};

/** Epoch ms when the token was issued, or null when the token carries no usable `iat`. */
export const getIssuedAtMs = (token) => {
  const iat = decodeJwtPayload(token)?.iat;
  return Number.isFinite(iat) ? iat * 1000 : null;
};

/** True when the token was issued for a full session (LOGIN scope), not for the steps before choosing an organization. */
export const hasLoginScope = (token) => {
  const scope = decodeJwtPayload(token)?.scope;
  return Array.isArray(scope) && scope.includes('LOGIN');
};

/** User the token was issued to (`sub`), or null when the token carries none. */
export const getSubject = (token) => decodeJwtPayload(token)?.sub ?? null;

/** Milliseconds left before the token expires. Returns null for tokens without `exp`. */
export const getMsUntilExpiry = (token) => {
  const expiryMs = getExpiryMs(token);
  return expiryMs === null ? null : expiryMs - Date.now();
};

/**
 * A token with no readable `exp` is treated as valid: an opaque token is the
 * server's business, and guessing "expired" would log the user out for free.
 */
export const isExpired = (token, skewMs = 0) => {
  const remaining = getMsUntilExpiry(token);
  return remaining === null ? false : remaining <= skewMs;
};
